import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { API } from "../../data/site";
import { inputCls, AdminButton } from "../ui";

const NavPanel = () => {
  const { data } = useQuery({
    queryKey: ["settings-admin"],
    queryFn: async () => (await axios.get(`${API}/settings`, { withCredentials: true })).data,
  });
  const [nav, setNav] = useState([]);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data?.nav) {
      setNav(JSON.parse(JSON.stringify(data.nav)));
      setSaved(JSON.parse(JSON.stringify(data.nav)));
    }
  }, [data]);

  const dirty = JSON.stringify(nav) !== JSON.stringify(saved);
  const update = (i, k, v) => setNav(nav.map((item, idx) => (idx === i ? { ...item, [k]: v } : item)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= nav.length) return;
    const next = [...nav];
    [next[i], next[j]] = [next[j], next[i]];
    setNav(next);
  };
  const remove = (i) => setNav(nav.filter((_, idx) => idx !== i));
  const add = () => setNav([...nav, { label: "New Link", to: "/", visible: true }]);

  const publish = async () => {
    const bad = nav.filter((n) => !n.label.trim() || !n.to.trim().startsWith("/"));
    if (bad.length) {
      toast.error("Every link needs a label and a path starting with / (e.g. /about)");
      return;
    }
    if (!window.confirm("Publish these navigation changes to the live website?")) return;
    setBusy(true);
    try {
      await axios.put(`${API}/settings`, { nav }, { withCredentials: true });
      toast.success("Navigation published");
      setSaved(JSON.parse(JSON.stringify(nav)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="nav-panel">
      <p className="text-sm text-[#5C5A56] max-w-2xl mb-8">
        Manage the menu at the top of the website. Nothing changes on the live site until you press
        "Publish Navigation". Use paths that start with / for pages on this website, or full https:// links.
      </p>
      <div className="flex flex-col gap-3 max-w-3xl">
        {nav.map((item, i) => (
          <div key={i} className="flex flex-wrap items-center gap-3 bg-white border border-[rgba(26,26,26,0.1)] p-4" data-testid={`nav-item-${i}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-20 hover:text-[#8C6A4F]" data-testid={`nav-up-${i}`} aria-label="Move up"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === nav.length - 1} className="p-1 disabled:opacity-20 hover:text-[#8C6A4F]" data-testid={`nav-down-${i}`} aria-label="Move down"><ArrowDown size={14} /></button>
            </div>
            <input value={item.label} onChange={(e) => update(i, "label", e.target.value)} className={`${inputCls} flex-1 min-w-[140px]`} placeholder="Label" data-testid={`nav-label-${i}`} />
            <input value={item.to} onChange={(e) => update(i, "to", e.target.value)} className={`${inputCls} flex-1 min-w-[160px] font-mono text-xs`} placeholder="/page-path" data-testid={`nav-to-${i}`} />
            <label className="flex items-center gap-2 font-mono text-[0.62rem] tracking-[0.12em] uppercase font-bold text-[#5C5A56]">
              <input type="checkbox" checked={item.visible !== false} onChange={(e) => update(i, "visible", e.target.checked)} className="w-4 h-4 accent-[#8C6A4F]" data-testid={`nav-visible-${i}`} />
              Show
            </label>
            <button onClick={() => remove(i)} className="p-2 hover:text-red-600" data-testid={`nav-delete-${i}`} aria-label="Remove"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <AdminButton onClick={add} testid="nav-add"><span className="inline-flex items-center gap-2"><Plus size={14} /> Add Link</span></AdminButton>
        <AdminButton primary type="button" onClick={publish} disabled={busy || !dirty} testid="nav-publish">
          {busy ? "Publishing…" : dirty ? "Publish Navigation" : "Navigation Published"}
        </AdminButton>
      </div>
      {dirty && <p className="mt-4 text-sm text-[#A37C45]" data-testid="nav-unsaved">You have unpublished navigation changes.</p>}
    </div>
  );
};

export default NavPanel;
